import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { BankDetailsFindInterface } from '@zaoblako/shared/modules/client/dtos/bank.details/bank-details-find.dto';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { BankStatementRuleConditionFormInterface } from '@zaoblako/shared/modules/document/dtos/bank-statement-rule/bank-statement-rule-condition-form.dto';
import type { BankStatementRuleExpressionFormInterface } from '@zaoblako/shared/modules/document/dtos/bank-statement-rule/bank-statement-rule-expression-form.dto';
import type { BankStatementRuleFindInterface } from '@zaoblako/shared/modules/document/dtos/bank-statement-rule/bank-statement-rule-find.dto';
import {
  BankStatementRuleFormDto,
  type BankStatementRuleFormInterface
} from '@zaoblako/shared/modules/document/dtos/bank-statement-rule/bank-statement-rule-form.dto';
import { BankStatementRuleConditionKindEnum } from '@zaoblako/shared/modules/document/enums/bank.statement.rule.condition.kind.enum';
import { BankStatementRuleExpressionTypeEnum } from '@zaoblako/shared/modules/document/enums/bank.statement.rule.expression.type.enum';
import { AccessService } from '@zaoblako/web/core/models/enums/access-rules/access.service';
import { BasisClientService } from '@zaoblako/web/core/services/basis/basis-client.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { DocumentRegulationService } from '@zaoblako/web/core/services/document/document-regulation.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { DirectionService } from '@zaoblako/web/core/services/finance/direction.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BankDetailsAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/bank-details-auto-complete/bank-details-auto-complete.component';
import { ContragentAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/contragent-auto-complete/contragent-auto-complete.component';
import { DealAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/deal-auto-complete/deal-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { BankStatementRuleConditionKindSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/bank-statement-rule-condition-kind-single-select/bank-statement-rule-condition-kind-single-select.component';
import { BankStatementRuleConditionTypeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/bank-statement-rule-condition-type-single-select/bank-statement-rule-condition-type-single-select.component';
import { BankStatementRuleJointTypeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/bank-statement-rule-joint-type-single-select/bank-statement-rule-joint-type-single-select.component';
import { CompanySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/company-single-select/company-single-select.component';
import { DirectionSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/direction-single-select/direction-single-select.component';
import { FilialSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/filial-single-select/filial-single-select.component';
import { UnitSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/unit-single-select/unit-single-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SkeletonModule } from 'primeng/skeleton';
import { flatten } from 'flat';
import { set } from 'lodash';
import { isNil } from 'lodash';
import { Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-document-regulation-form',
  templateUrl: './document-regulation-form.component.html',
  imports: [
    CommonModule,
    ButtonComponent,
    InputTextComponent,
    JoinPipe,
    CardModule,
    BankStatementRuleConditionKindSingleSelectComponent,
    BankStatementRuleConditionTypeSingleSelectComponent,
    BankDetailsAutoCompleteComponent,
    ContragentAutoCompleteComponent,
    DatepickerComponent,
    InputNumberModule,
    FormsModule,
    SkeletonModule,
    DividerModule,
    BankStatementRuleJointTypeSingleSelectComponent,
    CheckboxModule,
    SelectButtonModule,
    DirectionSingleSelectComponent,
    SpinnerComponent,
    DealAutoCompleteComponent,
    UnitSingleSelectComponent,
    FilialSingleSelectComponent,
    CompanySingleSelectComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentRegulationFormComponent
  extends BaseFormComponent<BankStatementRuleFormInterface>
  implements OnInit {
  @Input() public readonly index: number;
  public regulation = <BankStatementRuleFormInterface>{};
  public dto = BankStatementRuleFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public companies: CompanyFindInterface[] = [];
  public bankDetails: BankDetailsFindInterface[] = [];
  public directions: DirectionFindInterface[] = [];

  public excludeOptions = [
    { label: 'Загружать', value: false },
    { label: 'Не загружать', value: true }
  ];

  public get BankStatementRuleConditionKindEnum() {
    return BankStatementRuleConditionKindEnum;
  }

  public get BankStatementRuleExpressionTypeEnum() {
    return BankStatementRuleExpressionTypeEnum;
  }

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly documentRegulationService: DocumentRegulationService,
    private readonly basisClientService: BasisClientService,
    private readonly directionService: DirectionService,
    private readonly confirmService: ConfirmDialogService,
    private readonly errorService: ErrorService,
    public readonly accessService: AccessService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit() {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    this.directionService.find<DirectionFindInterface>().subscribe((directions) => {
      this.directions = directions;
      this.cdRef.markForCheck();
    });

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    } else {
      this.regulation.exclude = false;
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.documentRegulationService
      .findById<BankStatementRuleFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (regulation) => {
          this.regulation = regulation as any;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public get expressionsArrayError(): string | undefined {
    const expressions = (this.errors as Record<string, unknown> | null)?.expressions;
    return typeof expressions === 'string' ? expressions : undefined;
  }

  public getExpressionError(expressionIndex: number): string | undefined {
    const expressionError = this.getExpressionErrorItem(expressionIndex);
    if (!expressionError) {
      return undefined;
    }
    if (typeof expressionError === 'string') {
      return expressionError;
    }
    const messages = Object.values(this.flattenErrorMessages(expressionError));
    return messages.length ? messages.join('\n') : undefined;
  }

  public saveRegulation() {
    const validation = this.checkValidate(this.regulation);
    this.normalizeExpressionErrors();
    if (!validation.valid) {
      this.errorService.addCustomError(
        'Ошибка валидации',
        this.formatValidationErrorsForToast()
      );
      this.cdRef.markForCheck();
      return;
    }
    if (this.formStoreService.getState.forms[this.index]?.entityId) {
      this.formStoreService.setLoading(true, this.index);

      this.documentRegulationService
        .update<BankStatementRuleFormInterface, BankStatementRuleFindInterface>(this.regulation)
        .subscribe({
          next: (regulation) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Правило "${regulation?.name}" изменено`);
            this.formStoreService.saveNext(regulation, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
    } else {
      this.formStoreService.setLoading(true, this.index);

      this.documentRegulationService.create<BankStatementRuleFormInterface>(this.regulation).subscribe({
        next: (regulation) => {
          this.formStoreService.setLoading(false, this.index);
          this.errorService.addSuccessMessage('Правило создано');
          this.formStoreService.saveNext(regulation, this.index);
        },
        error: () => {
          this.formStoreService.setLoading(false, this.index);
        }
      });
    }
  }

  public deleteRegulation() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить правило "${this.regulation.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.documentRegulationService
          .deleteById(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe({
            next: () => {
              this.errorService.addSuccessMessage(`Правило "${this.regulation?.name}" удалено`);
              this.formStoreService.deleteNext(this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      }
    });
  }

  public searchBankDetails(name: string) {
    this.basisClientService.bankDetailsSearch({ search: name }).subscribe((bankDetails) => {
      this.bankDetails = bankDetails;
      this.cdRef.markForCheck();
    });
  }

  public addExpression() {
    if (!this.regulation.expressions) {
      this.regulation.expressions = [];
    }

    if (this.regulation.expressions.length) {
      const expressionOperator: BankStatementRuleExpressionFormInterface = {
        type: BankStatementRuleExpressionTypeEnum.OPERATOR
      };
      this.regulation.expressions.push(expressionOperator);
    }

    const expressionCondition: BankStatementRuleExpressionFormInterface = {
      condition: <BankStatementRuleConditionFormInterface>{},
      type: BankStatementRuleExpressionTypeEnum.CONDITION
    };
    this.regulation.expressions.push(expressionCondition);
  }

  public deleteExpression(expressionCondition: BankStatementRuleExpressionFormInterface, index: number) {
    let expressionOperator: BankStatementRuleExpressionFormInterface;
    if (index !== this.regulation.expressions?.length - 1) {
      expressionOperator = this.regulation.expressions[index + 1];
    } else {
      expressionOperator = this.regulation.expressions[index - 1];
    }
    this.regulation.expressions = this.regulation.expressions.filter((_expression) => {
      return _expression !== expressionCondition && _expression !== expressionOperator;
    });
    this.regulation.expressions = [...this.regulation.expressions];
  }

  private getExpressionErrorItem(expressionIndex: number): unknown {
    if (!this.errors) {
      return undefined;
    }
    const errors = this.errors as Record<string, unknown>;
    const expressions = errors.expressions;
    if (Array.isArray(expressions) && expressions[expressionIndex] !== undefined) {
      return expressions[expressionIndex];
    }
    return errors[`expressions[${expressionIndex}]`];
  }

  private normalizeExpressionErrors(): void {
    if (!this.errors) {
      return;
    }
    const errors = this.errors as Record<string, unknown>;
    const expressions: unknown[] = Array.isArray(errors.expressions)
      ? [...errors.expressions]
      : typeof errors.expressions === 'string'
        ? [errors.expressions]
        : [];

    for (const key of Object.keys(errors)) {
      const match = /^expressions\[(\d+)](?:\.(.+))?$/.exec(key);
      if (!match) {
        continue;
      }
      const index = Number(match[1]);
      const path = match[2];
      const value = errors[key];
      if (path) {
        if (!expressions[index] || typeof expressions[index] !== 'object') {
          expressions[index] = {};
        }
        set(expressions[index] as object, path, value);
      } else {
        expressions[index] = value;
      }
      delete errors[key];
    }

    if (expressions.length) {
      errors.expressions = expressions;
    } else if (typeof errors.expressions === 'string') {
      errors.expressions = [errors.expressions];
    }
  }

  private flattenErrorMessages(value: unknown): Record<string, string> {
    if (isNil(value)) {
      return {};
    }
    if (typeof value === 'string') {
      return { '': value };
    }
    if (typeof value !== 'object') {
      return { '': String(value) };
    }
    return flatten(value) as Record<string, string>;
  }

  private formatValidationErrorsForToast(): string {
    if (!this.errors) {
      return '';
    }
    const errors = this.errors as Record<string, unknown>;
    const lines: string[] = [];

    for (const [key, value] of Object.entries(errors)) {
      if (key === 'expressions' && Array.isArray(value)) {
        for (const [index, expressionError] of value.entries()) {
          if (isNil(expressionError)) {
            continue;
          }
          if (typeof expressionError === 'string') {
            lines.push(`[Условие ${index + 1}]: ${expressionError}`);
            continue;
          }
          for (const [path, message] of Object.entries(this.flattenErrorMessages(expressionError))) {
            lines.push(`[Условие ${index + 1}, ${path}]: ${message}`);
          }
        }
        continue;
      }
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        for (const [path, message] of Object.entries(this.flattenErrorMessages(value))) {
          lines.push(`[${key}${path ? `.${path}` : ''}]: ${message}`);
        }
        continue;
      }
      lines.push(`[${key}]: ${String(value)}`);
    }

    return lines.join('\n');
  }
}
